import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { locationService, Location } from '@/firebase/locationService';
import { complianceService } from '@/firebase/complianceService';
import { auditLogService } from '@/services/auditLogService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { getUserSystemRoleForOrganization, isAdminInOrganization } from '@/utils/roleHelpers';
import { isSuperAdmin } from '@/utils/adminUtils';
import { usePreview } from '@/contexts/PreviewContext';
import { getTasksForOrganization, TaskWithDetails } from '@/services/taskService';
import { Sparkles, RefreshCw, Map as MapIcon } from 'lucide-react';
import LocationForm from './LocationForm';
import AssignTemplatesPanel from './AssignTemplatesPanel';
import FloorManagementPanel from './FloorManagementPanel';
import ContextualHelp from './ContextualHelp';

const LocationManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { previewState, isPreviewMode } = usePreview();
  const { canAddLocation } = useSubscription();
  const [locations, setLocations] = useState<Location[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locationForTemplates, setLocationForTemplates] = useState<Location | null>(null);
  const [locationForFloors, setLocationForFloors] = useState<Location | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedState, setSelectedState] = useState<string>('All States');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalMessage, setUpgradeModalMessage] = useState<string>('');
  const [isSyncingTasks, setIsSyncingTasks] = useState(false);

  useEffect(() => {
    loadUserRole();
  }, [user?.id, currentOrganization?.id, currentOrganization?.name, isPreviewMode, previewState?.previewType, previewState?.role]);

  useEffect(() => {
    loadLocations();
    loadTemplates();
    loadTasks();
  }, [currentOrganization?.id, user?.id, isPreviewMode, previewState?.organizationId, previewState?.previewType, previewState?.previewUserId, previewState?.role, previewState?.locationIds]);

  const loadTasks = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      // Get tasks filtered by organization on server side
      const orgTasks = await getTasksForOrganization(currentOrganization.id);
      setTasks(orgTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  // Helper function to get task status for a template at a location
  const getTemplateStatus = (templateId: string, locationId: string): 'overdue' | 'due-soon' | 'not-due' | null => {
    if (!locationId || !templateId) return null;
    
    // Find tasks for this template and location
    const relevantTasks = tasks.filter(task => 
      task.templateId === templateId && 
      task.locationId === locationId &&
      task.status !== 'completed'
    );
    
    if (relevantTasks.length === 0) {
      // No active tasks - consider it "not due" (green)
      return 'not-due';
    }
    
    const now = new Date();
    const dueSoonDays = 30; // Tasks due within 30 days are "due soon"
    
    // Check if any task is overdue
    const hasOverdue = relevantTasks.some(task => {
      const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
      return task.status === 'overdue' || (dueDate < now && task.status !== 'completed');
    });
    
    if (hasOverdue) {
      return 'overdue';
    }
    
    // Check if any task is due soon
    const hasDueSoon = relevantTasks.some(task => {
      const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
      const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilDue >= 0 && daysUntilDue <= dueSoonDays && task.status === 'pending';
    });
    
    if (hasDueSoon) {
      return 'due-soon';
    }
    
    // Otherwise, not due yet
    return 'not-due';
  };

  // Helper function to get background color class based on status
  const getStatusColorClass = (status: 'overdue' | 'due-soon' | 'not-due' | null): string => {
    switch (status) {
      case 'overdue':
        return 'bg-red-100 text-red-700';
      case 'due-soon':
        return 'bg-orange-100 text-orange-700';
      case 'not-due':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-blue-100 text-blue-700'; // Default fallback
    }
  };

  const loadUserRole = async () => {
    if (!currentOrganization?.id) return;

    // In preview mode, use the previewed user's role
    if (isPreviewMode && previewState) {
      if (previewState.previewType === 'user') {
        const role = previewState.role?.toLowerCase();
        setUserRole(previewState.role || null);
        setIsAdmin(role === 'admin');
      } else {
        // Preview by role
        const role = previewState.role?.toLowerCase();
        setUserRole(previewState.role || null);
        setIsAdmin(role === 'admin');
      }
      return;
    }

    if (user?.id && currentOrganization?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific system role
          const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
          const isAdminForOrg = isAdminInOrganization(userData, organizationId);
          
          setUserRole(systemRole || null);
          setIsAdmin(isAdminForOrg);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization - fall back to legacy role
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          // Use organization-specific role if available
          if (currentOrganization?.id) {
            const role = getUserSystemRoleForOrganization(userData, currentOrganization.id);
            setUserRole(role || null);
            setIsAdmin(isAdminInOrganization(userData, currentOrganization.id));
          } else {
            // Fallback to legacy role if no organization
            const role = userData.role;
            setUserRole(role || null);
            setIsAdmin(role === 'admin');
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    }
  };

  const loadLocations = async () => {
    // Don't load locations if no organization
    if (!currentOrganization?.id) {
      setLocations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let userLocationIds: string[] | undefined;
    let userRoleForService: string | undefined;
    let effectiveUserId: string | undefined;

    if (isPreviewMode && previewState) {
      // Use previewed user's credentials
      if (previewState.previewType === 'user' && previewState.previewUserId) {
        effectiveUserId = previewState.previewUserId;
        userRoleForService = previewState.role;
        userLocationIds = previewState.locationIds;
      } else {
        // Preview by role: show all locations for support
        effectiveUserId = user?.id;
        userRoleForService = 'admin';
        userLocationIds = undefined;
      }
    } else if (user?.id) {
      // Normal mode: fetch real user's data
      effectiveUserId = user.id;
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          const organizationId = currentOrganization.id;
          userRoleForService = getUserSystemRoleForOrganization(userData, organizationId) || userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    // For LocationManagement, admins/managers see all, others see accessible only
    const result = await locationService.getAccessibleLocations(
      currentOrganization.id,
      effectiveUserId,
      userRoleForService,
      userLocationIds
    );
    if (result.success && result.data) {
      // Check for duplicates
      const locationIds = result.data.map(l => l.id).filter(Boolean);
      const uniqueIds = new Set(locationIds);
      
      if (locationIds.length !== uniqueIds.size) {
        console.warn(`⚠️ Found ${locationIds.length - uniqueIds.size} duplicate location IDs`);
        console.log('Location IDs:', locationIds);
        
        // Remove duplicates by keeping only the first occurrence
        const seen = new Set<string>();
        const uniqueLocations = result.data.filter(loc => {
          if (!loc.id) return true; // Keep locations without IDs
          if (seen.has(loc.id)) {
            console.log(`⚠️ Removing duplicate location: ${loc.name} (${loc.id})`);
            return false;
          }
          seen.add(loc.id);
          return true;
        });
        
        setLocations(uniqueLocations);
      } else {
        setLocations(result.data);
      }
    }
    setIsLoading(false);
  };

  const loadTemplates = async () => {
    const result = await complianceService.getAllTemplates(currentOrganization?.id);
    if (result.success && result.data) {
      setTemplates(result.data);
    }
  };

  const handleDelete = async (locationId: string) => {
    if (!isAdmin) {
      showNotification('Only admins can delete locations', 'error');
      return;
    }
    if (!confirm('Are you sure you want to delete this location?')) return;
    
    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    const result = await locationService.deleteLocation(
      locationId,
      userInfo,
      currentOrganization?.id
    );
    if (result.success) {
      await loadLocations();
      showNotification('Location deleted successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleEdit = (location: Location) => {
    if (!isAdmin) {
      showNotification('Only admins can edit locations', 'error');
      return;
    }
    setSelectedLocation(location);
    setIsEditing(true);
  };

  const handleAddClick = async () => {
    if (!isAdmin) {
      showNotification('Only admins can add locations', 'error');
      return;
    }

    // Check subscription limit before opening the form
    if (!currentOrganization?.id) {
      setUpgradeModalMessage('You must have an organization and active subscription to add locations. Please subscribe to a plan first.');
      setShowUpgradeModal(true);
      return;
    }

    const currentLocationCount = locations.length;
    const canAddResult = await canAddLocation(currentLocationCount);
    
    if (!canAddResult.canAdd) {
      const errorMsg = canAddResult.error === 'Location limit reached' 
        ? `You have reached your location limit (${canAddResult.maxLocations} locations). Please upgrade your subscription to add more locations.`
        : canAddResult.error || 'Cannot add location. Please check your subscription.';
      setUpgradeModalMessage(errorMsg);
      setShowUpgradeModal(true);
      return;
    }

    // If we can add, open the form
    setIsAdding(true);
  };

  const handleAddLocation = async (locationData: Partial<Location>) => {
    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    if (!isAdmin) {
      showNotification('Only admins can add locations', 'error');
      return;
    }

    // Check subscription limit before adding location
    if (!currentOrganization?.id) {
      setUpgradeModalMessage('You must have an organization and active subscription to add locations. Please subscribe to a plan first.');
      setShowUpgradeModal(true);
      setIsAdding(false);
      return;
    }

    const currentLocationCount = locations.length;
    const canAddResult = await canAddLocation(currentLocationCount);
    
    if (!canAddResult.canAdd) {
      const errorMsg = canAddResult.error === 'Location limit reached' 
        ? `You have reached your location limit (${canAddResult.maxLocations} locations). Please upgrade your subscription to add more locations.`
        : canAddResult.error || 'Cannot add location. Please check your subscription.';
      setUpgradeModalMessage(errorMsg);
      setShowUpgradeModal(true);
      setIsAdding(false);
      return;
    }

    const result = await locationService.addLocation(
      locationData as Omit<Location, 'id'>, 
      currentOrganization?.id,
      userInfo
    );
    if (result.success && result.id) {
      // Track location added
      const { trackOrganization } = await import('@/utils/analytics');
      trackOrganization.locationAdded(result.id, currentOrganization?.id || '');
      // Automatically assign the new location to the current user
      if (user?.id) {
        try {
          const { doc, updateDoc, getDoc, serverTimestamp } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const currentLocationIds = userData.locationIds || [];
            
            // Only add if not already in the array
            if (!currentLocationIds.includes(result.id)) {
              await updateDoc(userRef, {
                locationIds: [...currentLocationIds, result.id],
                updatedAt: serverTimestamp()
              });
              console.log(`✅ Automatically assigned location ${result.id} to user ${user.id}`);
            }
          }
        } catch (error) {
          console.error('Error assigning location to user:', error);
          // Don't fail the entire operation if user assignment fails
          showNotification('Location added, but failed to assign to user automatically', 'info');
        }
      }
      
      await loadLocations();
      setIsAdding(false);
      setSelectedLocation(null);
      showNotification('Location added and assigned to you successfully', 'success');
      
      // After adding location, subscription quantity should be updated via webhook when payment processes
      // or we could trigger an update here if needed
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleUpdateLocation = async (locationData: Partial<Location>) => {
    if (!isAdmin) {
      showNotification('Only admins can update locations', 'error');
      return;
    }
    if (!selectedLocation?.id) {
      showNotification('Error: Location ID not found', 'error');
      return;
    }
    
    const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
    const result = await locationService.updateLocation(
      selectedLocation.id, 
      locationData,
      userInfo,
      currentOrganization?.id
    );
    if (result.success) {
      await loadLocations();
      setIsEditing(false);
      setSelectedLocation(null);
      showNotification('Location updated successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleFormSubmit = async (locationData: Partial<Location>) => {
    if (isEditing) {
      await handleUpdateLocation(locationData);
    } else {
      await handleAddLocation(locationData);
    }
  };

  const handleFormCancel = () => {
    setIsAdding(false);
    setIsEditing(false);
    setSelectedLocation(null);
  };

  const handleAssignTemplates = (location: Location) => {
    setLocationForTemplates(location);
  };

  const handleManageFloors = (location: Location) => {
    setLocationForFloors(location);
  };

  const handleCloseFloorsPanel = () => {
    setLocationForFloors(null);
  };

  const handleViewFloorMap = (floorId: string) => {
    const locId = locationForFloors?.id;
    setLocationForFloors(null);
    if (locId) navigate(`/floor-map?locationId=${locId}&floorId=${floorId}`);
  };

  const handleSaveTemplatesAssignment = async (templateIds: string[]) => {
    if (!locationForTemplates?.id || !currentOrganization?.id) return;
    
    // Get old assigned templates for audit log
    const oldTemplateIds = locationForTemplates.assignedTemplates || [];
    const addedTemplateIds = templateIds.filter(id => !oldTemplateIds.includes(id));
    const removedTemplateIds = oldTemplateIds.filter(id => !templateIds.includes(id));
    
    console.log('📝 Audit log - Template assignment change:', {
      locationId: locationForTemplates.id,
      locationName: locationForTemplates.name,
      oldTemplateIds,
      newTemplateIds: templateIds,
      addedTemplateIds,
      removedTemplateIds,
    });
    
    const result = await locationService.updateLocation(
      locationForTemplates.id, 
      {
        assignedTemplates: templateIds,
      },
      undefined, // Don't log as location update
      undefined
    );
    
    if (result.success) {
      // Log audit entries for compliance assignments/removals
      const userInfo = user ? { id: user.id, name: user.name, email: user.email } : undefined;
      
      if (!userInfo) {
        console.warn('⚠️ Cannot log audit entry: user info not available');
      }
      
      if (userInfo) {
        try {
          // Get compliance names for audit logs
          const allTemplates = await complianceService.getAllTemplates(currentOrganization.id);
          const templateMap = new Map<string, string>();
          if (allTemplates.success && allTemplates.data) {
            allTemplates.data.forEach(t => {
              if (t.id) templateMap.set(t.id, t.name);
            });
          }
          
          // Log added compliances
          for (const templateId of addedTemplateIds) {
            const templateName = templateMap.get(templateId) || 'Unknown';
            const logResult = await auditLogService.log({
              action: 'compliance_assigned',
              entityType: 'compliance_assignment',
              entityId: `${locationForTemplates.id}-${templateId}`,
              entityName: `${templateName} → ${locationForTemplates.name}`,
              userId: userInfo.id,
              userName: userInfo.name,
              userEmail: userInfo.email,
              organizationId: currentOrganization.id,
              details: {
                locationId: locationForTemplates.id,
                locationName: locationForTemplates.name,
                complianceId: templateId,
                complianceName: templateName,
              },
            });
            if (!logResult.success) {
              console.error('❌ Failed to log compliance_assigned:', logResult.error);
            } else {
              console.log('✅ Logged compliance_assigned:', templateName);
            }
          }
          
          // Log removed compliances
          for (const templateId of removedTemplateIds) {
            const templateName = templateMap.get(templateId) || 'Unknown';
            const logResult = await auditLogService.log({
              action: 'compliance_removed',
              entityType: 'compliance_assignment',
              entityId: `${locationForTemplates.id}-${templateId}`,
              entityName: `${templateName} → ${locationForTemplates.name}`,
              userId: userInfo.id,
              userName: userInfo.name,
              userEmail: userInfo.email,
              organizationId: currentOrganization.id,
              details: {
                locationId: locationForTemplates.id,
                locationName: locationForTemplates.name,
                complianceId: templateId,
                complianceName: templateName,
              },
            });
            if (!logResult.success) {
              console.error('❌ Failed to log compliance_removed:', logResult.error);
            } else {
              console.log('✅ Logged compliance_removed:', templateName);
            }
          }
        } catch (error) {
          console.error('❌ Error logging audit entries:', error);
        }
      }
      
      await loadLocations();
      showNotification('Compliances assigned successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
  };

  const handleCloseTemplatesPanel = () => {
    setLocationForTemplates(null);
  };

  const handleSyncMissingTasks = async () => {
    if (!currentOrganization?.id || !user?.email || !isSuperAdmin(user.email)) return;
    setIsSyncingTasks(true);
    try {
      const functions = getFunctions();
      const backfill = httpsCallable<{ organizationId: string; locationId?: string }, { success: boolean; created: number; message?: string; errors?: string[] }>(functions, 'backfillMissingComplianceTasks');
      const result = await backfill({ organizationId: currentOrganization.id });
      const data = result.data as { success: boolean; created: number; message?: string; errors?: string[] };
      if (data.success) {
        showNotification(data.created > 0 ? `Created ${data.created} missing task(s)` : 'No missing tasks found', 'success');
        if (data.created > 0) {
          await loadTasks();
          await loadLocations();
        }
        if (data.errors?.length) {
          console.warn('Some tasks could not be created:', data.errors);
        }
      } else {
        showNotification(data.message || 'Sync failed', 'error');
      }
    } catch (err: any) {
      showNotification(err?.message || 'Failed to sync missing tasks', 'error');
    } finally {
      setIsSyncingTasks(false);
    }
  };

  // Get unique states for filtering
  const states = Array.from(new Set(locations.map(l => l.state))).sort();

  // Filter locations
  const filteredLocations = locations.filter(location => {
    const matchesState = selectedState === 'All States' || location.state === selectedState;
    const matchesSearch = !searchTerm || 
      location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.country.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesState && matchesSearch;
  });

  return (
    <div className="p-4 max-w-[95vw] mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-800">Business Locations</h2>
            <ContextualHelp category="Locations" tooltip="Help with Locations" />
          </div>
          <div className="flex gap-2">
            {user?.email && isSuperAdmin(user.email) && (
              <button
                onClick={handleSyncMissingTasks}
                disabled={isSyncingTasks}
                className="btn-secondary px-4 py-2 flex items-center gap-2"
                title="Create tasks for locations that have compliances assigned but no pending task (Admin Support only)"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncingTasks ? 'animate-spin' : ''}`} />
                {isSyncingTasks ? 'Syncing...' : 'Sync missing tasks'}
              </button>
            )}
            {isAdmin && (
            <button onClick={handleAddClick} className="btn-primary px-4 py-2">
              + Add Location
            </button>
            )}
            <button onClick={loadLocations} className="btn-secondary px-4 py-2">
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
          />
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading locations...</p>
          </div>
        ) : (
          <>
            {/* States Filter */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Filter by State</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedState('All States')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedState === 'All States'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All States
                </button>
                {states.map(state => (
                  <button
                    key={state}
                    onClick={() => setSelectedState(state)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedState === state
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Showing {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} 
                {selectedState !== 'All States' && ` in ${selectedState}`}
              </p>
            </div>

            {/* Locations Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Location Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Address</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">City</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">State</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Country</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Assigned Compliances</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map((location, index) => (
                    <tr key={location.id || `location-${index}`} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="font-medium text-sm text-gray-900">{location.name}</div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{location.address}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{location.city || 'N/A'}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{location.state}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <span className="text-xs text-gray-700">{location.country}</span>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex flex-wrap gap-1 max-w-md">
                          {location.assignedTemplates && location.assignedTemplates.length > 0 ? (
                            templates.filter(t => location.assignedTemplates?.includes(t.id)).map((template) => {
                              const status = getTemplateStatus(template.id, location.id || '');
                              const colorClass = getStatusColorClass(status);
                              return (
                                <span key={template.id} className={`${colorClass} px-1.5 py-0.5 rounded text-xs whitespace-nowrap`}>
                                  {template.name}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-xs text-gray-400">None assigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => handleAssignTemplates(location)} 
                            className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                            title="Assign Compliances"
                          >
                            📌
                          </button>
                          <button 
                            onClick={() => handleManageFloors(location)} 
                            className="text-purple-600 hover:text-purple-800 text-xs px-2 py-1"
                            title="Manage Floors"
                          >
                            <MapIcon className="w-4 h-4 inline" />
                          </button>
                          {isAdmin && (
                          <button 
                            onClick={() => location && handleEdit(location)} 
                            className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          )}
                          {isAdmin && (
                          <button 
                            onClick={() => location.id && handleDelete(location.id)} 
                            className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                            title="Delete"
                          >
                            🗑️
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredLocations.length === 0 && (
              <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
                <p className="text-lg font-medium">No locations found</p>
                <p className="text-sm mt-1">{searchTerm && 'Try a different search term or clear filters.'}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {(isAdding || isEditing) && (
        <LocationForm
          location={selectedLocation || undefined}
          templates={templates}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      {/* Floor Management Panel */}
      {locationForFloors && (
        <FloorManagementPanel
          location={locationForFloors}
          onClose={handleCloseFloorsPanel}
          onViewFloorMap={handleViewFloorMap}
        />
      )}

      {/* Assign Compliances Panel */}
      {locationForTemplates && (
        <AssignTemplatesPanel
          location={locationForTemplates}
          templates={templates}
          locations={locations}
          onAssign={handleSaveTemplatesAssignment}
          onClose={handleCloseTemplatesPanel}
        />
      )}
      {locations.length === 0 && !isLoading && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Location Limit Reached</h3>
                <p className="text-sm text-gray-600">Upgrade Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              {upgradeModalMessage}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/subscription');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationManagement;

